import type { Role } from '~/models';

export const rolesDict: Record<Role, string> = {
  raffle_admin: 'Raffle Admin',
  demo_admin: 'Demo Admin',
  partner_admin: 'Partner Admin',
  backstage_admin: 'Backstage Admin',
  account_admin: 'Account Admin',
  knowledgebase_manager: 'KB Manager',
  insighter: 'Insighter',
  user: 'User',
};

export const rolesAbstraction: Record<Role, Role[]> = {
  raffle_admin: [
    'raffle_admin',
    'account_admin',
    'backstage_admin',
    'knowledgebase_manager',
    'insighter',
    'user',
  ],
  demo_admin: [
    'demo_admin',
    'account_admin',
    'backstage_admin',
    'knowledgebase_manager',
    'insighter',
    'user',
  ],
  partner_admin: [
    'partner_admin',
    'account_admin',
    'backstage_admin',
    'knowledgebase_manager',
    'insighter',
    'user',
  ],
  backstage_admin: ['backstage_admin', 'user'],
  account_admin: [
    'account_admin',
    'knowledgebase_manager',
    'insighter',
    'user',
  ],
  knowledgebase_manager: ['knowledgebase_manager', 'insighter', 'user'],
  insighter: ['insighter', 'user'],
  user: ['user'],
};

export function expandUserRoles(userRoles: Role[]): Role[] {
  const expandedRoles = new Set<Role>();

  userRoles.forEach((role) => {
    const mappedRoles = rolesAbstraction[role] ?? [role];
    mappedRoles.forEach((r) => expandedRoles.add(r));
  });

  return Array.from(expandedRoles);
}

export function parseRole(userRoles: Role[]): Role {
  const roleKeys = Object.keys(rolesAbstraction) as Role[];

  for (let i = 0; i < roleKeys.length; i++) {
    const key = roleKeys[i];

    if (userRoles.includes(key)) {
      return key;
    }
  }

  return 'user';
}

export const roleGroups = {
  raffleAdmin: [
    'raffle_admin',
    'demo_admin',
    'new_raffle_admin',
    'new_demo_admin',
  ] as Role[],
  backstageUser: ['new_backstage_admin', 'backstage_admin'] as Role[],
  accountAdmin: ['account_admin'] as Role[],
};

export function hasGroupRole(
  userRoles: Role[],
  group: keyof typeof roleGroups,
): boolean {
  const expanded = expandUserRoles(userRoles);
  return expanded.some((r) => roleGroups[group].includes(r));
}

export const isRaffleAdmin = (userRoles: Role[]) =>
  hasGroupRole(userRoles, 'raffleAdmin');

export const isBackstageUser = (userRoles: Role[]) =>
  hasGroupRole(userRoles, 'backstageUser');

export const isAccountAdmin = (userRoles: Role[]) =>
  hasGroupRole(userRoles, 'accountAdmin');

export const roleOptions = Object.entries(rolesDict).map(([value, label]) => ({
  value: value as Role,
  label,
}));

export function filterRoleOptions(userRoles: Role[]) {
  return roleOptions.filter((s) => {
    // Noone can assign raffle admin or backend-only roles
    if ([...raffleAdminRoles, ...backendOnlyRoles].includes(s.value))
      return false;

    // Only raffle admins can assign partner admin
    if ('partner_admin' === s.value) return isRaffleAdmin(userRoles);

    // Include only roles that the user has
    return userRoles.includes(s.value);
  });
}

type HasRolePermissionProps = {
  roles: Role[];
  userRoles: Role[];
};

export function hasRolePermission({
  roles,
  userRoles,
}: HasRolePermissionProps) {
  const expanded = expandUserRoles(userRoles);
  return roles.length ? expanded.some((r) => roles.includes(r)) : true;
}

export type RouteType =
  | 'insights'
  | 'backstage'
  | 'sources'
  | 'tools'
  | 'settings'
  | 'api'
  | 'home'
  | 'rerank'
  | 'feedback';

export const routePermissions: Record<RouteType, Role[]> = {
  insights: ['knowledgebase_manager', 'insighter'],
  backstage: ['backstage_admin'],
  sources: ['knowledgebase_manager'],
  tools: ['account_admin'],
  rerank: ['knowledgebase_manager'],
  settings: ['account_admin'],
  api: ['account_admin'],
  home: ['insighter'],
  feedback: ['insighter'],
};

export const backendOnlyRoles: Role[] = ['backstage_admin', 'user'];

export const raffleAdminRoles: Role[] = ['raffle_admin', 'demo_admin'];

export const adminRoles: Role[] = [
  'account_admin',
  'partner_admin',
  ...raffleAdminRoles,
];
